import { Capacitor } from '@capacitor/core'
import { SplashScreen } from '@capacitor/splash-screen'
import {
    TOrgTheme,
    getDevicePref,
    setSpLogLvl,
    spErrorTags,
    spLogTags,
} from '@eventr/common'
import {
    ColoursObj,
    LoadingSpinner,
    generateTheme,
} from '@eventr/common-storybook'
import { ThemeProvider, createTheme } from '@mui/material'
import { captureException } from '@sentry/capacitor'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { SafeArea } from 'capacitor-plugin-safe-area'
import { useEffect, useState } from 'react'
import { deviceDetect } from 'react-device-detect'
import { ErrorBoundary } from 'react-error-boundary'
import { I18nextProvider } from 'react-i18next'
import { registerListeners } from './feat/appState/appStateListener'
import './feat/appState/deviceInfo'
import {
    setStatusBarStyleDark,
    setStatusBarStyleLight,
} from './feat/appState/statusBar'
import './feat/connectivity/connectivityListener'
import { DevRoutesSplit } from './feat/developerMenu/devRoutesSplit'
import { ErrorPage } from './feat/developerMenu/errorPage'
import { setCollectionEnabled } from './feat/firebase/firebase'
import { GuestRoutesSplit } from './feat/guest/guestRouteSplit'
import { createChatStore } from './feat/rocketchat/chatStore'
import { RocketChatProvider } from './feat/rocketchat/rocketChatProvider'
import { themeOverride } from './feat/themeOverrides'
import { initializeI18n } from './i18n/i18next'
import { AppRoutes } from './routes'
import { initSentry } from './sentry'
import { useBoundStore } from './state/bound.store'
import { useLanguageContext } from './state/languageState/languageContext'
import { OverlayWrapper } from './state/overlayState/overlayWrapper'
import { ScrollWrapper } from './state/scrollState/scrollWrapper'
import { useStorageStore } from './state/storage/storage.store'
import { useThemeContext } from './state/themeState/themeContext'
import { windowSizes } from './state/windowState/windowSignals'
import { delay, getDevicePrefName } from './utils'

function setDefaultStage() {
    spLogTags('Current app version : ' + import.meta.env.VITE_APP_VERSION, [
        'App',
        'Version',
    ])
    if (localStorage.getItem('REACT_APP_STAGE')) return
    console.log('no stage in localstorage set to demo')
    if (import.meta.env.VITE_APP_STAGE) {
        localStorage.setItem('REACT_APP_STAGE', import.meta.env.VITE_APP_STAGE)
        return
    }
    localStorage.setItem('REACT_APP_STAGE', 'demo')
}
registerListeners()
initSentry()

function App() {
    const hasHydrated = useStorageStore((s) => s.hasHydrated)

    if (Capacitor.isNativePlatform()) {
        if (Capacitor.getPlatform() === 'android') {
            SafeArea.setImmersiveNavigationBar()
        }
        delay(2000).then(() => {
            SplashScreen.hide()
        })
        setCollectionEnabled(true)
    }

    setDefaultStage()
    setSpLogLvl(0)
    createChatStore()

    spLogTags('Start of App', ['App'])

    const [mergedTheme, setMergedTheme] = useState(
        createTheme(generateTheme(ColoursObj) as any, themeOverride())
    )
    const selectedColourScheme = useThemeContext((s) => s.selectedColourScheme)
    const selectedCustomThemeId = useThemeContext((s) => s.customThemeId)
    const themeImages = useThemeContext((s) => s.themeImages)
    const setDefault = useThemeContext((s) => s.setDefaultTheme)
    const preferedTheme: TOrgTheme = getDevicePref(
        'themePalette',
        getDevicePrefName(deviceDetect)
    )
    const language = useLanguageContext()

    const orgConfig = useBoundStore((s) => s.orgConfig)

    useEffect(() => {
        const defaultTheme = (orgConfig?.themes || [])?.find((theme) => {
            return theme.default
        })

        if (defaultTheme) {
            setDefault(defaultTheme)
        }

        if (!useThemeContext.getState().getOverride() && !preferedTheme) {
            const setPreferredColorScheme = () => {
                if (defaultTheme) {
                    useThemeContext
                        .getState()
                        .setThemeImages(defaultTheme?.images)
                    useThemeContext.getState().setScheme('custom', defaultTheme)
                } else {
                    const query = window.matchMedia(
                        '(prefers-color-scheme: dark)'
                    )

                    if (Capacitor.isNativePlatform()) {
                        if (query.matches) {
                            setStatusBarStyleDark()
                        } else {
                            setStatusBarStyleLight()
                        }
                    }
                    useThemeContext.getState().setCustomId('')

                    const preferredColorScheme = query.matches
                        ? 'dark'
                        : 'light'

                    spLogTags('preferredColorScheme: ' + preferredColorScheme, [
                        'Theme',
                    ])
                    useThemeContext.getState().setScheme(preferredColorScheme)
                    if (Object.keys(themeImages).length > 0) {
                        useThemeContext.getState().setThemeImages({})
                    }
                }
            }

            setPreferredColorScheme()

            window
                .matchMedia('(prefers-color-scheme: dark)')
                .addEventListener('change', setPreferredColorScheme)
        } else {
            useThemeContext.getState().setOverride(true)
            useThemeContext.getState().setCustomId(preferedTheme?._id || '')

            useThemeContext
                .getState()
                .setScheme(
                    preferedTheme ? 'custom' : selectedColourScheme,
                    preferedTheme
                )
        }

        setMergedTheme(
            createTheme(generateTheme(ColoursObj) as any, themeOverride())
        )
    }, [selectedColourScheme, selectedCustomThemeId, orgConfig, themeImages])

    useEffect(() => {
        if (windowSizes.value.insets?.insets) {
            setMergedTheme(
                createTheme(generateTheme(ColoursObj) as any, themeOverride())
            )
        }
    }, [windowSizes.value.insets?.insets])

    async function setI18nContext() {
        const i18nContext = await initializeI18n()
        language.setI18nContext(i18nContext)
    }

    if (!language.i18nContext) {
        setI18nContext()
        console.log('App.tsx i18nContext is not set')
        return <LoadingSpinner size={'32px'} type="static" />
    }

    if (hasHydrated !== 'finished') {
        console.log('App.tsx hasHydrated is not finished')

        return <LoadingSpinner size={'32px'} />
    }
    console.log('App.tsx rendering')
    const queryClient = new QueryClient()
    return (
        <ThemeProvider theme={mergedTheme}>
            <I18nextProvider i18n={language.i18nContext}>
                <ScrollWrapper>
                    <DevRoutesSplit>
                        <ErrorBoundary
                            fallback={<ErrorPage />}
                            onError={(error) => {
                                spErrorTags(error, ['ERROR', 'ErrorBoundary'])
                                captureException(error, {
                                    extra: {
                                        note:
                                            'Created from ErrorBoundary. error: ' +
                                            JSON.stringify(error),
                                    },
                                })
                            }}
                        >
                            {' '}
                            <QueryClientProvider client={queryClient}>
                                <RocketChatProvider>
                                    <OverlayWrapper>
                                        <GuestRoutesSplit>
                                            <AppRoutes />
                                        </GuestRoutesSplit>
                                    </OverlayWrapper>
                                </RocketChatProvider>
                            </QueryClientProvider>
                        </ErrorBoundary>
                    </DevRoutesSplit>
                </ScrollWrapper>
            </I18nextProvider>
        </ThemeProvider>
    )
}

export default App
